import { NavLink } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { articles } from '../data/article1';

function Paragraphe({ children }) {
  return <p className='lecture-article__text u-justify-text'>{children}</p>;
}

function Gras({ children }) {
  return <b>{children}</b>;
}

function Title({ children }) {
  return <h2 className=' heading-infos color-black u-margin-top-medium'>{children}</h2>;
}

function List({ children }) {
  return <ul className='u-margin-left lecture-article__text'>{children}</ul>;
}

function ListItem({ children }) {
  return <li>{children}</li>;
}

function OrderedList({ children }) {
  return <ol className='u-margin-left lecture-article__text'>{children}</ol>;
}

export default function Article02() {
  return (
    <>
      <Navigation />
      <div className='lecture-article'>
        <NavLink to='/articles' className='retour'>
          <div className='retour__icon--down-arrow'>
            <img src='/icons/left-arrow.svg' alt='icone dun chevron' />
          </div>
          <p className='retour__text'>Retour aux articles</p>
        </NavLink>
        <h2 className='lecture-article__title heading-section color-black u-margin-top-medium '>
          Thrulkhors et corps subtil
        </h2>
        <p className='lecture-article__sous-titre '>Lecture de 10 min | 17/02/2025</p>
        <img
          src={articles[1].src}
          alt='article 1'
          className='lecture-article__img correction-img-article-2'
        />
        <Paragraphe>
          Dans la médecine traditionnelle tibétaine, aussi appelée « Sowa Rigpa » ou Science de la
          guérison, le corps, comme l&apos;univers, est composé de <Gras>cinq éléments</Gras>{' '}
          (terre, eau, feu, air et espace) regroupés en <Gras>trois humeurs</Gras> (vent, bile et
          phlegme). La santé repose sur l&apos;équilibre de ces trois humeurs. Les blocages
          physiques, émotionnels et mentaux, ainsi que les problèmes de santé, sont symptomatiques
          de déséquilibres à ce niveau. Les Thrulkhors aident à rétablir cet équilibre en agissant
          sur le <Gras>corps subtil</Gras>.
        </Paragraphe>

        <Title>Le corps subtil</Title>
        <Paragraphe>
          Dans le bouddhisme comme dans le bön, le corps, la parole et l’esprit sont les trois «
          portes » par lesquelles s’accomplissent les pratiques. La porte de la parole représente
          l’énergie. Celle-ci se manifeste dans nos vies à travers la respiration, les sensations,
          les émotions, le niveau de vitalité et constitue ce que la tradition nomme le « corps
          subtil ». <Gras>Ce corps subtil, interface entre le corps et la conscience</Gras>, est
          représenté sous la forme de <Gras>canaux</Gras>, de <Gras>vents</Gras> et de{' '}
          <Gras>gouttes vitales</Gras> sur lesquels nous pouvons nous connecter et intervenir au
          travers de la pratique :{' '}
          <List>
            <ListItem>
              <Gras>Les canaux</Gras> – tsa en tibétain : 72 000 canaux parcourent le corps subtil.
              Ils sont analogues au système nerveux du corps physique. Les trois principaux canaux
              utilisés dans la pratique sont le canal central et les deux canaux latéraux. Le{' '}
              <Gras>canal central</Gras> part du sommet du crâne et descend jusqu’aux organes
              génitaux. Les <Gras>canaux latéraux</Gras>, quant à eux, partent de chaque narine,
              suivent la voûte crânienne jusqu’au sommet de la tête, puis descendent verticalement
              le long du canal central avant de se connecter entre eux et à ce dernier sous le
              nombril.
            </ListItem>
            <ListItem>
              <Gras>Les vents</Gras> – lung en tibétain : à l’intérieur du réseau des canaux subtils
              circulent des <Gras>énergies appelées vents</Gras>, particulièrement connectées à la
              respiration.
            </ListItem>
            <ListItem>
              <Gras>Les gouttes vitales</Gras> – thigle en tibétain : traditionnellement
              représentées sous la forme de <Gras>gouttes de lumière</Gras>, elles sont considérées
              comme le fondement de l’<Gras>l’énergie vitale</Gras> et de la <Gras>conscience</Gras>{' '}
              dans le corps subtil. Elles circulent dans le réseau de canaux grâce aux vents.
            </ListItem>
          </List>
        </Paragraphe>

        <Title> Les cinq éléments et les chakras</Title>
        <Paragraphe>
          Dans le corps subtil, les 5 éléments et les émotions sont connectés aux{' '}
          <Gras>chakras, signifiant « roues » ou vortex d’énergie</Gras>. Ce sont des plexus
          psychiques formés au niveau des intersections entre les canaux latéraux et le canal
          central. Le déséquilibre des éléments et les émotions perturbatrices impactent le corps
          subtil, et au final, se manifestent dans le corps physique sous la forme de maladies.
        </Paragraphe>
        <OrderedList>
          <ListItem>
            <Gras>La terre</Gras> (Chakra du nombril - orgueil): elle représente la
            <Gras> stabilité et la solidité</Gras>. Cet élément assure la structure et le soutien du
            corps physique via les tissus, les os, les muscles, les tendons, les organes et la peau.
            En équilibre, il apporte stabilité physique, mentale et émotionnelle. En excès, il
            entraîne lourdeur, paresse et attachement. En déficit, il provoque faiblesse, fragilité
            des os et insécurité.
          </ListItem>
          <ListItem>
            <Gras>L'eau</Gras> (Chakra du cœur - colère/aversion) : elle représente la
            <Gras> fluidité et la cohésion</Gras>. Elle hydrate et lubrifie les articulations et les
            organes via les liquides corporels : sang, urine, salive et lymphe. En équilibre, elle
            assure souplesse et cohérence. En excès, elle provoque rétention d’eau, œdèmes et excès
            de mucus. En déficit, elle entraîne déshydratation et sécheresse de la peau et des
            muqueuses.
          </ListItem>
          <ListItem>
            <Gras>Le feu</Gras> (Chakra de la gorge – désir/attachement) : il représente la
            <Gras> chaleur et la transformation</Gras>. Cet élément maintient la température
            corporelle et régule la digestion ainsi que le métabolisme. En équilibre, il stimule ces
            fonctions et apporte clarté mentale et enthousiasme. En excès, il provoque
            inflammations, fièvre, irritabilité et brûlures d’estomac. En déficit, il ralentit la
            digestion et diminue la vitalité.
          </ListItem>
          <ListItem>
            <Gras>L'air</Gras>(Chakra génital - jalousie) : il représente le
            <Gras> mouvement et l’énergie</Gras>. Cet élément influence la respiration, la
            circulation sanguine et la mobilité. En équilibre, il favorise l’élimination des déchets
            et soutient les fonctions dynamiques. En excès, il entraîne anxiété, agitation,
            ballonnements et tremblements. En déficit, il provoque stagnation physique et mentale.
          </ListItem>
          <ListItem>
            <Gras>L’espace</Gras> (Chakra de la tête- ignorance) : il représente
            <Gras> l’expansion et l’ouverture</Gras> . Cet élément permet aux autres de fonctionner.
            En excès, il provoque dispersion mentale et sentiment de vide. En déficit, il entraîne
            oppression et blocages internes.
          </ListItem>
        </OrderedList>

        <Title> Les trois humeurs et les maladies</Title>
        <Paragraphe>
          Le corps subtil peut également être appréhendé à travers les humeurs :
          <Gras> le vent, la bile et le phlegme </Gras>correspondant respectivement aux essences
          vitales (thiglés), aux énergies (lungs) et aux canaux (tsas). Les éléments et émotions y
          sont associés et synthétisés dans une classification en trois niveaux :
        </Paragraphe>
        <List>
          <ListItem>
            le <Gras>flegme </Gras>(békan en tibétain) associé à la <Gras>terre </Gras>et à{' '}
            <Gras>l’eau</Gras>.
          </ListItem>
          <ListItem>
            la <Gras>bile </Gras> (tripa en tibétain) : associée au <Gras>feu</Gras>.{' '}
          </ListItem>
          <ListItem>
            le <Gras>vent </Gras>(lung en tibétain) : associé à <Gras>l’air</Gras> et à{' '}
            <Gras>l’espace</Gras>.
          </ListItem>
        </List>
        <Paragraphe>
          Selon le Sowa Rigpa, il existe trois grandes catégories de maladies, appelées Nyepa
          (défaut, erreur), relatives aux trois humeurs, influencées par trois émotions
          perturbatrices :
        </Paragraphe>
        <List>
          <ListItem>Rlung-Nyepa (maladies liées au vent) - ignorance</ListItem>
          <ListItem>Mkrhis-Nyepa (maladies liées à la bile) - colère/aversion</ListItem>
          <ListItem>Bad-Kan-Nyepa (maladies liées au flegme) - désir/attachement</ListItem>
        </List>

        <Title>Dissiper les obstacles</Title>
        <Paragraphe>
          Les blocages sur le plan subtil causent des maladies sur le plan physique, humoral, tandis
          que la purification du plan subtil entraîne la réalisation de corps spirituels appelés «
          Kaya » dans le bouddhisme tibétain.{' '}
          <Gras>
            C’est la dimension interne des Thrulkhors, qui permet de réduire les blocages physiques
            et émotionnels en équilibrant les énergies circulant dans le corps subtil.
          </Gras>
        </Paragraphe>
        <Paragraphe>
          La pratique des Thrulkhors assouplit les canaux, facilitant ainsi la libre circulation des
          énergies. Le travail respiratoire et les visualisations aident à diffuser les vents dans
          tout le réseau de canaux, atteignant ainsi certaines parties du corps insuffisamment
          pourvues. Enfin, dans la posture de méditation assise, avec la colonne vertébrale droite,
          les canaux sont ouverts et étirés, rendant possible l’harmonisation des énergies subtiles.
          Le corps devient stable et détendu, les perturbations émotionnelles et mentales
          s’apaisent, permettant ainsi, dans la contemplation, la reconnaissance de la nature de
          l’esprit. De nombreux maîtres Dzogchen ont atteint l’éveil complet par cette pratique.
        </Paragraphe>

        <Title>Entretenir la santé</Title>
        <Paragraphe>
          Même si la perfection de la réalisation spirituelle n'est pas atteinte,
          <Gras> la pratique des Thrulkhors permet d'alléger les blocages énergétiques</Gras>. En
          rendant les canaux plus souples et en soutenant la libre circulation des vents et des
          gouttes vitales dans le corps subtil, elle contribue à l’harmonisation des cinq éléments
          et des trois humeurs, favorisant ainsi une bonne santé et une longue vie.
        </Paragraphe>

        <NavLink
          to='/stages-et-retraites/stage-2'
          className='btn stage__btn btn--white u-center-text u-center-element u-margin-top-medium '
        >
          Participer à un stage
        </NavLink>

        <Title>Sources</Title>
        <List>
          <li>
            <b>Lama Dukte, Tempa</b>. Thrul Kor Ancient Tibetan Bon Yoga. Olmo Ling Publications,
            2019. 260 pages.
          </li>
          <li>
            <b>Chaoul, Alejandro</b>. Le yoga tibétain au quotidien. Éditions Médicis, 2021. 118
            pages.
          </li>
          <li>
            <b>BERZIN, Alexander, Ph.D</b>. Médecine tibétaine : principes de base [en ligne]. Study
            Buddhism, date de publication non spécifiée,[consulté le 8 février 2025]. Disponibilité
            et accès{' '}
            <a href='https://studybuddhism.com/fr/etudes-avancees/histoire-culture/medecine-tibetaines/medecine-tibetaine-principes-de-base'>
              ici
            </a>
          </li>
          <li>
            <b> Clifford, Terry</b>. La Médecine tibétaine bouddhique et sa psychiatrie : La
            Thérapie de diamant. Préface de Sa Sainteté le Dalaï-Lama, introduction de Lokesh
            Chandra. Paris : Dervy, 1997, 338 pages.{' '}
          </li>
        </List>
      </div>
    </>
  );
}
